import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  SectionIntro,
  FeatureGrid,
  FeatureRow,
  StatBand,
  Steps,
  Quote,
  LogoCloud,
  CTASection,
  Reveal,
  Mock,
  Button,
  Icon,
} from './kit.jsx'

const FEATURES = [
  {
    icon: 'boxes',
    title: '10,900+ verified leads',
    body: 'Every company, verified, cleaned, and organized by industry and region — with contacts, service area, size, and annual volume.',
    tag: 'Live data',
  },
  {
    icon: 'filter',
    title: 'Filter to your best-fit accounts',
    body: 'Slice the database by region, industry, company size, and volume so reps only ever work the accounts worth their time.',
  },
  {
    icon: 'rocket',
    title: 'Multi-step outreach sequences',
    body: 'Build Apollo-style email cadences with waits and branches, then send from your own Gmail — replies land right back in your inbox.',
  },
  {
    icon: 'chart',
    title: 'Deal flow & pipeline',
    body: 'Track every company from new to won across a people-level funnel, with activity and revenue signals rolled up automatically.',
  },
  {
    icon: 'sparkle',
    title: 'Answers from your live data',
    body: 'Ask the built-in assistant “top regions this quarter” or “how is outreach doing” and get answers straight from your workspace.',
  },
  {
    icon: 'users',
    title: 'Built for teams',
    body: 'Invite reps, assign regions, and keep everyone working the same source of truth with role-based collaboration.',
  },
]

const STATS = [
  { value: '10,942', label: 'Verified B2B leads' },
  { value: '50', label: 'U.S. regions covered' },
  { value: '13', label: 'Regions, ready on day one' },
  { value: '<1s', label: 'To load any region' },
]

const STEPS = [
  {
    title: 'Pick your market',
    body: 'Choose an industry and region and instantly see every qualified company — no scraping, no stale lists, no spreadsheets.',
  },
  {
    title: 'Build a sequence',
    body: 'Drop the accounts you want into a multi-step email cadence and connect your Gmail to send as yourself.',
  },
  {
    title: 'Work the pipeline',
    body: 'Track replies, move deals through the funnel, and let the dashboard surface where the revenue is.',
  },
]

export default function LandingPage() {
  return (
    <MarketingShell active="home">
      <Seo
        title="Verified B2B leads, outreach, and pipeline"
        description="Revailo Sales is the lead engine for B2B teams — verified companies in your market, multi-step outreach, and pipeline tracking in one workspace."
        path="/"
        keywords="b2b leads, sales prospecting, cold email outreach, sales pipeline, lead enrichment"
      />

      <PageHero
        eyebrow="The B2B lead engine"
        title={
          <>
            Win more deals, <br className="hidden sm:inline" />
            from first lead to closed revenue.
          </>
        }
        lead="Revailo Sales puts verified companies in your market, multi-step outreach, and pipeline tracking in one workspace — so your team spends time selling, not sourcing."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Book a demo', to: '/demo' }}
      >
        <Reveal>
          <Mock kind="dashboard" />
        </Reveal>
      </PageHero>

      <Section sm>
        <Container>
          <LogoCloud
            label="Built for teams that sell every day"
            logos={['Masonry', 'Construction', 'Building Materials', 'Professional Services', 'Manufacturing']}
          />
        </Container>
      </Section>

      <Section id="features">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Everything in one place"
              title={<>The whole sales motion, without the busywork.</>}
              lead="Sourcing, enrichment, outreach, and pipeline — Revailo Sales replaces the patchwork of lists, inboxes, and spreadsheets sales teams juggle today."
            />
          </Reveal>
          <div className="mt-14">
            <Reveal>
              <FeatureGrid items={FEATURES} cols={3} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section className="py-14">
        <Container>
          <Reveal>
            <StatBand stats={STATS} />
          </Reveal>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="flex flex-col" style={{ gap: 96 }}>
            <FeatureRow
              eyebrow="A real database"
              title="Every company in your market, already qualified"
              body="Skip the scraping. Revailo Sales ships with the full verified database — company, location, industry, service area, established year, size, annual volume, and named contacts — organized so your reps can filter to their best-fit accounts in seconds."
              bullets={[
                '10,942 verified companies, cleaned and de-duplicated',
                'Sortable, searchable tables with real contact details',
                'Region-scoped so reps focus on their territory',
              ]}
              media={
                <Reveal>
                  <Mock kind="leads" />
                </Reveal>
              }
            />
            <FeatureRow
              reverse
              eyebrow="Outreach that sends itself"
              title="Multi-step sequences from your own inbox"
              body="Build cadences with intro emails, waits, follow-ups, and break-ups. Connect Gmail once and send as yourself — replies come back to your inbox and get logged against the account automatically."
              bullets={[
                'Apollo-style sequence builder with steps and waits',
                'Send + receive through your real Gmail account',
                'Every send and reply tracked on the timeline',
              ]}
              media={
                <Reveal>
                  <Mock kind="sequence" />
                </Reveal>
              }
            />
            <FeatureRow
              eyebrow="Know what's working"
              title="Pipeline and revenue signals, live"
              body="Deal Flow shows every company moving from new to won. Activity and Data views roll up the numbers so you always know which regions, lead types, and campaigns are actually producing revenue."
              bullets={[
                'People-level funnel from new to closed-won',
                'Region, lead-quality, and campaign breakdowns',
                'Ask the assistant questions in plain English',
              ]}
              media={
                <Reveal>
                  <Mock kind="analytics" />
                </Reveal>
              }
            />
          </div>
        </Container>
      </Section>

      <Section className="py-14">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="How it works"
              title="Live in an afternoon, not a quarter"
              lead="No data project, no long onboarding. Sign in and your market is already loaded."
            />
          </Reveal>
          <div className="mt-10" style={{ maxWidth: 760, margin: '40px auto 0' }}>
            <Reveal>
              <Steps items={STEPS} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2 min-[960px]:grid-cols-3">
            <Reveal>
              <Quote
                quote="We stopped buying junk lead lists. Everything's already here and our reps just work their region."
                name="Marcus Bell"
                role="Owner, Bell Analytics"
              />
            </Reveal>
            <Reveal delay={80}>
              <Quote
                quote="Sequences from our own Gmail doubled our reply rate versus the blast tool we used before."
                name="Dana Ruiz"
                role="Sales Lead, Summit Health"
              />
            </Reveal>
            <Reveal delay={160}>
              <Quote
                quote="The pipeline view finally tells us which regions are worth the drive. That alone paid for it."
                name="Tariq Nolan"
                role="VP Sales, Ridgeline Exteriors"
              />
            </Reveal>
          </div>
        </Container>
      </Section>

      <CTASection
        title="Your next 100 jobs are already in the database."
        body="Start free and see every qualified company in your market today. No credit card, no data project."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Talk to sales', to: '/contact' }}
      />
    </MarketingShell>
  )
}
